/**
 * What this browser calls itself, for the whole tab.
 *
 * The same arrangement as lib/operator.ts and for the same reason: two screens
 * in different branches of the tree need to agree about one small value, and
 * there is a router between them. The chat panel has always been where a name
 * is picked, because until there was one there was nothing else to do in a
 * room; the profile screen is where somebody goes to look at who they are, and
 * a page with your name at the top of it and no way to change it is a page
 * that has told you it is not really yours. Both write here.
 *
 * ## Only for a learner who is not signed in
 *
 * A signed-in name is the account's. It lives on the row, the Worker hands it
 * to the room on the upgrade, and the room ignores what the browser claims
 * (worker/room.ts, docs/cheating.md) — so there is nothing for a store in the
 * tab to own, and renaming is a PUT and a refresh (state/useAccount.ts). This
 * is the other half: a name kept in `engineeros.chat.v1` beside the id it goes
 * with, mirrored into the vault with it, and true only of this browser.
 *
 * That split is why the two halves are not one function with a flag in it. The
 * failure modes have nothing in common: this one cannot fail, and the other
 * one is a round trip that can be refused.
 *
 * ## Null is a real state
 *
 * It means nobody has picked a name on this browser, and in a room it means a
 * **listener** — somebody reading without having joined. That is a state the
 * chat protocol has an opinion about, so `rename` never invents a name to get
 * out of it: an empty field is refused by `cleanName` and the caller shows the
 * rule, exactly as the join prompt does.
 */
import { cleanName } from '@engineer-os/chat';
import { chatPrefs, updatePrefs } from './chat';

/** Notified when the name changes: the open sockets, and anything drawing it. */
const listeners = new Set<() => void>();

/**
 * The name this browser has picked, or null for none.
 *
 * Reads through `chatPrefs`, which is itself cached, and hands back a string —
 * so `useSyncExternalStore` compares by value and needs no cache of its own,
 * unlike the operator store whose value is read out of a fresh object.
 */
export function hereName(): string | null {
  return chatPrefs().name;
}

export function onName(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function announce(): void {
  for (const fn of [...listeners]) fn();
}

/**
 * Be called this on this browser, or null for nobody.
 *
 * The cleaned name, or null when the rule refuses it — which the caller shows
 * rather than swallows. `cleanName` is the room's own rule and is the same
 * function the server checks with, so a name accepted here is never refused
 * there.
 *
 * Nothing is sent from this file. Announcing is the whole of what it does, and
 * the frames go out from the hook that owns the sockets (state/useChat.ts),
 * the way a saved operator does.
 */
export function rename(raw: string | null): string | null {
  const next = raw === null ? null : cleanName(raw);
  if (next === null && raw !== null) return null;
  if (next === hereName()) return next;
  updatePrefs({ name: next });
  announce();
  return next;
}

/**
 * Adopt a name that arrived from somewhere other than a rename — the vault,
 * one recovery per load (lib/chat.ts, `recoverPrefs`).
 *
 * It is already in the preferences by the time this is called; what is missing
 * is that anybody was told. Separate from {@link rename} because it must not
 * write: a recovery that wrote back what it had just read would mirror the
 * blob a second time on every load, and because the value it carries has
 * already been through `cleanName` on the way in.
 */
export function adoptName(): void {
  announce();
}
